import { useState } from 'react';
import { z } from 'zod';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Stack,
  useColorModeValue,
  Text,
  Flex,
} from '@chakra-ui/react';
import { FaFileAlt } from 'react-icons/fa';
import SelectSearch from 'components/fields/SelectSearch';
import { PROVINCES } from 'variables/options';
import type { SelectOption } from 'components/fields/SelectSearch';
import type { APICarrierData } from 'types/api.types';
import {
  dashReportSchema,
  initialFormData,
  type DashReportFormData,
  type DashReportFormErrors,
} from '../schemas/dashReportSchema';

interface DashReportFormProps {
  carrierOptions: SelectOption[];
  carriers: APICarrierData[];
  isLoadingCarriers: boolean;
  onSubmit: (data: DashReportFormData) => Promise<void>;
  submitButtonText?: string;
  isSubmitting?: boolean;
  defaultCarrier?: APICarrierData | null;
  disableCarrierSelection?: boolean;
}

const DashReportForm = ({
  carrierOptions,
  carriers,
  isLoadingCarriers,
  onSubmit,
  submitButtonText = 'Generate DASH Report',
  isSubmitting: externalIsSubmitting,
  defaultCarrier = null,
  disableCarrierSelection = false,
}: DashReportFormProps) => {
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const [formData, setFormData] = useState({
    ...initialFormData,
    carrier: defaultCarrier,
  });
  const [formErrors, setFormErrors] = useState<DashReportFormErrors>({});
  const [internalIsSubmitting, setInternalIsSubmitting] = useState(false);

  // Use external isSubmitting if provided, otherwise use internal state
  const isSubmitting =
    externalIsSubmitting !== undefined
      ? externalIsSubmitting
      : internalIsSubmitting;

  // Clear field error when user types
  const handleFieldChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Validate single field
  const validateField = (field: keyof typeof formData) => {
    try {
      const fieldSchema = dashReportSchema.shape[field];
      fieldSchema.parse(formData[field]);
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (error) {
      if (error instanceof z.ZodError) {
        const zodError = error as z.ZodError;
        setFormErrors((prev) => ({
          ...prev,
          [field]: zodError.issues[0]?.message,
        }));
      }
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setFormErrors({});

    try {
      // Validate form data with Zod
      const validatedData = dashReportSchema.parse({
        province: formData.province || '',
        licenseNumber: formData.licenseNumber,
        carrier: formData.carrier,
      });

      // Only manage internal state if not controlled externally
      if (externalIsSubmitting === undefined) {
        setInternalIsSubmitting(true);
      }

      await onSubmit(validatedData);
      resetForm();
    } catch (submitError) {
    } finally {
      // Only manage internal state if not controlled externally
      if (externalIsSubmitting === undefined) {
        setInternalIsSubmitting(false);
      }
    }
  };

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <Stack spacing="6">
          {/* Province Selection */}
          <FormControl isRequired isInvalid={!!formErrors.province}>
            <FormLabel color={textColor}>Province</FormLabel>
            <SelectSearch
              options={PROVINCES}
              value={formData.province}
              onChange={(value) => {
                handleFieldChange('province', value as string | null);
              }}
              placeholder="Select province"
              allowClear
            />
            {formErrors.province && (
              <FormErrorMessage>{formErrors.province}</FormErrorMessage>
            )}
          </FormControl>

          {/* License Number */}
          <FormControl isRequired isInvalid={!!formErrors.licenseNumber}>
            <FormLabel color={textColor}>License Number</FormLabel>
            <Input
              value={formData.licenseNumber}
              onChange={(e) => {
                handleFieldChange('licenseNumber', e.target.value);
              }}
              onBlur={() => validateField('licenseNumber')}
              placeholder="Enter driver's license number (e.g., A1234-56789-01234)"
              borderColor={borderColor}
              _hover={{ borderColor: 'gray.400' }}
              _focus={{ borderColor: 'brand.500' }}
              isInvalid={!!formErrors.licenseNumber}
            />
            {formErrors.licenseNumber && (
              <FormErrorMessage>{formErrors.licenseNumber}</FormErrorMessage>
            )}
          </FormControl>

          {/* Carrier Selection */}
          <FormControl isInvalid={!!formErrors.carrier}>
            <FormLabel color={textColor}>
              Carrier
              {isLoadingCarriers && (
                <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                  (Loading carriers...)
                </Text>
              )}
            </FormLabel>
            <SelectSearch
              options={carrierOptions}
              value={formData.carrier?.id || null}
              onChange={(value) => {
                const selectedCarrier =
                  carriers.find((c) => c.id === value) || null;
                handleFieldChange('carrier', selectedCarrier);
              }}
              placeholder={defaultCarrier?.name ?? 'Select carrier'}
              isLoading={isLoadingCarriers}
              allowClear
              isDisabled={disableCarrierSelection}
            />
            {formErrors.carrier && (
              <FormErrorMessage>{formErrors.carrier}</FormErrorMessage>
            )}
          </FormControl>

          {/* Submit Buttons */}
          <Flex justify="flex-end" gap={4}>
            <Button
              variant="outline"
              colorScheme="gray"
              onClick={resetForm}
              isDisabled={isSubmitting}
            >
              Reset Form
            </Button>
            <Button
              type="submit"
              colorScheme="brand"
              isLoading={isSubmitting}
              loadingText="Generating Report..."
              leftIcon={<FaFileAlt />}
            >
              {submitButtonText}
            </Button>
          </Flex>
        </Stack>
      </form>
    </Box>
  );
};

export default DashReportForm;
